import { CaretLeft, Door, FileText, SquaresFour } from '@phosphor-icons/react';
import { eur } from '../../../lib/immobilien/format';
import { fotoHintergrund } from '../../../lib/immobilien/url';
import { objektAdresse, objektName } from '../../../lib/immobilien/objektKopf';
import type { EinheitNavEintrag, ObjektOrt } from '../../../lib/immobilien/sidebarNav';
import type { KontextZustand } from '../../../lib/immobilien/useObjektKontext';
import { ANGEBUNDENE_PANELS, panelsFuer } from '../objektPanels';
import { SidebarDock } from './SidebarDock';
import { NavZeile, UnterZeile } from './NavZeile';

const MAX_UNTERPUNKTE = 14;
const OHNE_HAUS = '__ohne';

export function ObjektModus({ ort, zustand, panel, haus, onNavigate }: {
  ort: ObjektOrt;
  zustand: KontextZustand;
  /** ?panel=<key> der aktuellen URL */
  panel: string | null;
  /** ?haus=<id> der aktuellen URL */
  haus: string | null;
  onNavigate: (pfad: string) => void;
}) {
  const basis = `/admin/objekte/${encodeURIComponent(ort.objektId)}`;
  const daten = zustand.status === 'ok' ? zustand.daten : null;
  const einheiten = daten?.einheiten ?? [];
  const haeuser = daten?.haeuser ?? [];
  const mitHaeusern = haeuser.length > 0;

  // Haus der geöffneten Einheit (damit die Liste beim Einheitenwechsel nicht springt)
  const einheitHaus = ort.einheitId ? einheiten.find(e => e.id === ort.einheitId)?.hausId ?? OHNE_HAUS : null;
  const gewaehltesHaus = haus ?? (mitHaeusern ? einheitHaus : null);
  const ohneHausAnzahl = einheiten.filter(e => !e.hausId).length;

  const einheitZeile = (e: EinheitNavEintrag) => (
    <UnterZeile
      key={e.id}
      label={e.bezeichnung}
      punkt={e.vermietet ? 'var(--ok)' : 'var(--warn)'}
      rechts={e.vermietet ? eur(e.miete, { vorzeichen: true }) : 'frei'}
      aktiv={ort.einheitId === e.id}
      onClick={() => onNavigate(`${basis}/einheiten/${encodeURIComponent(e.id)}`)}
    />
  );

  const unterpunkte = () => {
    if (ort.bereich !== 'einheiten') return null;
    if (mitHaeusern && !gewaehltesHaus) {
      return (
        <>
          {haeuser.map(h => (
            <UnterZeile key={h.id} label={h.bezeichnung} punkt="var(--acc)" rechts={`${h.anzahl} WE`} aktiv={false}
              onClick={() => onNavigate(`${basis}/einheiten?haus=${encodeURIComponent(h.id)}`)} />
          ))}
          {ohneHausAnzahl > 0 && (
            <UnterZeile label="Ohne Haus" punkt="var(--ink3)" rechts={`${ohneHausAnzahl} WE`} aktiv={false}
              onClick={() => onNavigate(`${basis}/einheiten?haus=${OHNE_HAUS}`)} />
          )}
        </>
      );
    }
    const liste = gewaehltesHaus
      ? einheiten.filter(e => (gewaehltesHaus === OHNE_HAUS ? !e.hausId : e.hausId === gewaehltesHaus))
      : einheiten;
    const hausName = gewaehltesHaus === OHNE_HAUS ? 'Ohne Haus' : haeuser.find(h => h.id === gewaehltesHaus)?.bezeichnung;
    return (
      <>
        {gewaehltesHaus && hausName && (
          <UnterZeile label={`← ${hausName}`} punkt="transparent" rechts="" aktiv onClick={() => onNavigate(`${basis}/einheiten`)} />
        )}
        {liste.slice(0, MAX_UNTERPUNKTE).map(einheitZeile)}
        {liste.length > MAX_UNTERPUNKTE && (
          <UnterZeile label={`+ ${liste.length - MAX_UNTERPUNKTE} weitere`} punkt="transparent" rechts="" aktiv={false}
            onClick={() => onNavigate(`${basis}/einheiten`)} />
        )}
      </>
    );
  };

  const kopf = daten?.kopf;
  const nummer = kopf?.objektnummer?.trim();
  const chip = [nummer, daten?.verwaltungsart].filter(Boolean).join(' · ');

  return (
    <div className="immo-side-in flex flex-col flex-1 min-h-0" style={{ gap: 12 }}>
      <button
        type="button"
        onClick={() => onNavigate('/admin/objekte')}
        className="immo-hover-flaeche flex items-center self-start cursor-pointer"
        style={{ height: 30, padding: '0 12px 0 8px', borderRadius: 15, border: 'none', gap: 5, background: 'var(--fill2)', fontSize: 12, fontWeight: 700, color: 'var(--ink)' }}
      >
        <CaretLeft weight="bold" size={12} />
        Alle Immobilien
      </button>

      <button
        type="button"
        onClick={() => onNavigate(basis)}
        aria-label={kopf ? `${objektName(kopf)} – Übersicht` : 'Objekt-Übersicht'}
        className="immo-innen flex flex-col overflow-hidden cursor-pointer text-left"
        style={{ borderRadius: 18, border: 'none', padding: 0, color: 'var(--ink)' }}
      >
        <div className="relative w-full" style={{ height: 60, background: fotoHintergrund(kopf?.bild_url) ?? 'var(--fill2)' }}>
          {!fotoHintergrund(kopf?.bild_url) && (
            <div className="absolute inset-0" style={{ background: 'repeating-linear-gradient(135deg,var(--fill) 0 10px,transparent 10px 20px)' }} />
          )}
          {chip && (
            <span className="absolute flex items-center"
              style={{ left: 8, bottom: 8, height: 22, padding: '0 8px', borderRadius: 11, fontSize: 11, fontWeight: 700, background: 'var(--chip)', color: 'var(--chip-ink)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
              {chip}
            </span>
          )}
        </div>
        <div className="flex flex-col min-w-0 w-full" style={{ padding: '10px 12px', gap: 1 }}>
          {zustand.status === 'laedt' && <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Lädt …</span>}
          {zustand.status === 'fehler' && <span style={{ fontSize: 12, color: 'var(--bad)' }}>Objekt konnte nicht geladen werden.</span>}
          {zustand.status === 'fehlt' && <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Objekt nicht gefunden.</span>}
          {kopf && (
            <>
              <span className="truncate" style={{ fontSize: 14, fontWeight: 800, letterSpacing: '-0.01em' }}>{objektName(kopf)}</span>
              <span className="truncate" style={{ fontSize: 11, color: 'var(--ink3)' }}>{objektAdresse(kopf)}</span>
            </>
          )}
        </div>
      </button>

      <nav aria-label="Objektnavigation" className="immo-ohne-scrollbar flex flex-col flex-1 min-h-0 overflow-auto" style={{ gap: 1, margin: '0 -4px', padding: '0 4px' }}>
        <NavZeile art="objekt" label="Übersicht" icon={SquaresFour} aktiv={ort.bereich === 'uebersicht' && !panel} onClick={() => onNavigate(basis)} />
        <NavZeile
          art="objekt"
          label={mitHaeusern ? 'Häuser & Einheiten' : 'Einheiten'}
          icon={Door}
          aktiv={ort.bereich === 'einheiten'}
          anzahl={daten ? einheiten.length : null}
          onClick={() => onNavigate(`${basis}/einheiten`)}
        />
        {unterpunkte()}
        <NavZeile art="objekt" label="Abrechnung" icon={FileText} aktiv={ort.bereich === 'abrechnung'} onClick={() => onNavigate(`${basis}/abrechnung`)}
          gesperrt="Nebenkostenabrechnung – noch nicht angebunden" />

        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink3)', padding: '12px 9px 4px' }}>Objekt</span>
        {panelsFuer(daten?.verwaltungsart ?? null).map(p => (
          <NavZeile
            key={p.key}
            art="objekt"
            label={p.label}
            icon={p.icon}
            aktiv={panel === p.key}
            onClick={() => onNavigate(`${basis}?panel=${p.key}`)}
            gesperrt={ANGEBUNDENE_PANELS.has(p.key) ? undefined : 'Noch nicht angebunden – folgt mit der neuen Objektseite'}
          />
        ))}
      </nav>

      <SidebarDock aktiv="objekte" onNavigate={onNavigate} />
    </div>
  );
}
